import { useEnhancedClickOutside } from '../../hooks/useEnhancedClickOutside'
import React from 'react'
import { type Publication } from '../../services/publicationService'
import { useScrollPosition } from '../../hooks/useScrollPosition'

interface PublicationsDeleteConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  publication: Publication | null
  isLoading?: boolean
}

const PublicationsDeleteConfirmationModal: React.FC<PublicationsDeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  publication,
  isLoading = false
}) => {
  // Use the scroll lock hook
  useScrollPosition(isOpen)

  // Use enhanced click outside functionality
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useEnhancedClickOutside(onClose)

  if (!isOpen || !publication) return null

  return (
    <div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>
          
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Publication
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete the publication <strong>"{publication.title}"</strong>? 
              This action cannot be undone.
            </p>
            
            <div className="flex space-x-3 justify-center">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PublicationsDeleteConfirmationModal
